// Dimension constants and utilities for passport photo printing.
//
// All dimensions are calculated at 300 DPI for print quality.
//

// Print resolution in dots per inch.
//
export const DPI = 300;

const MILLIMETERS_PER_INCH = 25.4;

// Converts inches to pixels at print DPI.
//
export function inchesToPixels(inches: number): number {
  return Math.round(inches * DPI);
}

// Converts millimeters to pixels at print DPI.
//
export function millimetersToPixels(millimeters: number): number {
  return inchesToPixels(millimeters / MILLIMETERS_PER_INCH);
}

// Available passport photo sizes.
//
export type PhotoSize = "2x2" | "33x48";

interface PhotoSizeConfig {
  label: string;
  alternateLabel: string;
  application: string;
  inches: { width: number; height: number };
  millimeters: { width: number; height: number };
  pixels: { width: number; height: number };
  head: {
    label: string;
    minPercent: number;
    maxPercent: number;
    guideTopPercent: number;
    targetTopPercent: number;
    width?: {
      label: string;
      minPercent: number;
      maxPercent: number;
    };
  };
}

// Passport photo dimensions and positioning guides.
//
// The 2x2-inch profile follows U.S. Department of State requirements. The
// 33x48-millimeter profile follows Chinese passport and visa requirements.
//
export const PHOTO_SIZES: Record<PhotoSize, PhotoSizeConfig> = {
  "2x2": {
    label: "2 × 2 inches",
    alternateLabel: "51 × 51 mm",
    application: "U.S. passport",
    inches: { width: 2, height: 2 },
    millimeters: { width: 50.8, height: 50.8 },
    pixels: { width: inchesToPixels(2), height: inchesToPixels(2) },
    head: {
      label: '1–1⅜"',
      minPercent: 50,
      maxPercent: 69,
      guideTopPercent: 10,
      targetTopPercent: 12,
    },
  },
  "33x48": {
    label: "33 × 48 mm",
    alternateLabel: '1.30" × 1.89"',
    application: "Chinese passport / travel document",
    inches: {
      width: 33 / MILLIMETERS_PER_INCH,
      height: 48 / MILLIMETERS_PER_INCH,
    },
    millimeters: { width: 33, height: 48 },
    pixels: {
      width: millimetersToPixels(33),
      height: millimetersToPixels(48),
    },
    head: {
      label: "28–33 mm",
      minPercent: (28 / 48) * 100,
      maxPercent: (33 / 48) * 100,
      guideTopPercent: (4 / 48) * 100,
      targetTopPercent: (4 / 48) * 100,
      width: {
        label: "15–22 mm",
        minPercent: (15 / 33) * 100,
        maxPercent: (22 / 33) * 100,
      },
    },
  },
};

// Gap configuration for arranging photos on a print.
//
export const GAP_CONFIG = {
  min: 20, // Minimum gap (~0.07 inch at 300 DPI)
  max: 100, // Maximum gap (~0.33 inch at 300 DPI)
  default: 75, // Default gap (~0.25 inch at 300 DPI)
  minEdgeMargin: 30, // Minimum edge margin to avoid print cutoff (~0.1 inch)
};

// Available print sizes.
//
export type PrintSize =
  | "3.5x5"
  | "4x4"
  | "4x5.3"
  | "4x6"
  | "5x7"
  | "6x8"
  | "8x8"
  | "8x10"
  | "8x12";

// Available final download formats.
//
export type OutputSize = "digital" | PrintSize;

interface PrintSizeConfig {
  label: string;
  inches: { width: number; height: number };
  pixels: { width: number; height: number };
}

// Print paper dimensions.
//
export const PRINT_SIZES: Record<PrintSize, PrintSizeConfig> = {
  "3.5x5": {
    label: '3.5" × 5"',
    inches: { width: 3.5, height: 5 },
    pixels: { width: inchesToPixels(3.5), height: inchesToPixels(5) },
  },
  "4x4": {
    label: '4" × 4"',
    inches: { width: 4, height: 4 },
    pixels: { width: inchesToPixels(4), height: inchesToPixels(4) },
  },
  "4x5.3": {
    label: '4" × 5.3"',
    inches: { width: 4, height: 5.3 },
    pixels: { width: inchesToPixels(4), height: inchesToPixels(5.3) },
  },
  "4x6": {
    label: '4" × 6"',
    inches: { width: 4, height: 6 },
    pixels: { width: inchesToPixels(4), height: inchesToPixels(6) },
  },
  "5x7": {
    label: '5" × 7"',
    inches: { width: 5, height: 7 },
    pixels: { width: inchesToPixels(5), height: inchesToPixels(7) },
  },
  "6x8": {
    label: '6" × 8"',
    inches: { width: 6, height: 8 },
    pixels: { width: inchesToPixels(6), height: inchesToPixels(8) },
  },
  "8x8": {
    label: '8" × 8"',
    inches: { width: 8, height: 8 },
    pixels: { width: inchesToPixels(8), height: inchesToPixels(8) },
  },
  "8x10": {
    label: '8" × 10"',
    inches: { width: 8, height: 10 },
    pixels: { width: inchesToPixels(8), height: inchesToPixels(10) },
  },
  "8x12": {
    label: '8" × 12"',
    inches: { width: 8, height: 12 },
    pixels: { width: inchesToPixels(8), height: inchesToPixels(12) },
  },
};

export interface PrintLayout {
  grid: { columns: number; rows: number };
  photoCount: number;
  totalPixels: { width: number; height: number };
  offset: { x: number; y: number };
}

// Calculates the largest centered photo grid that fits on the selected paper.
//
export function calculatePrintLayout(
  printSize: PrintSize,
  photoSize: PhotoSize,
  photoGap: number = GAP_CONFIG.default,
): PrintLayout {
  const print = PRINT_SIZES[printSize];
  const photo = PHOTO_SIZES[photoSize];
  const availableWidth = print.pixels.width - GAP_CONFIG.minEdgeMargin * 2;
  const availableHeight = print.pixels.height - GAP_CONFIG.minEdgeMargin * 2;

  // Adding one gap before division accounts for the missing trailing gap.
  //
  const columns = Math.max(
    1,
    Math.floor((availableWidth + photoGap) / (photo.pixels.width + photoGap)),
  );
  const rows = Math.max(
    1,
    Math.floor((availableHeight + photoGap) / (photo.pixels.height + photoGap)),
  );
  const totalWidth = photo.pixels.width * columns + photoGap * (columns - 1);
  const totalHeight = photo.pixels.height * rows + photoGap * (rows - 1);

  return {
    grid: { columns, rows },
    photoCount: columns * rows,
    totalPixels: { width: totalWidth, height: totalHeight },
    offset: {
      x: Math.round((print.pixels.width - totalWidth) / 2),
      y: Math.round((print.pixels.height - totalHeight) / 2),
    },
  };
}
